export type MobSentinelScope = 'active_incidents' | 'all_incidents' | 'alerts';
export type MobSentinelSeverity = 'all' | 'critical' | 'high' | 'medium' | 'low';
export type MobEstateDrilldownKind = 'provider' | 'gap' | 'freshness' | 'sentinel';

export type MobEstateDrilldownRequest = {
  id: number;
  kind: MobEstateDrilldownKind;
  value: string;
  label: string;
  preferredLens?: 'architecture' | 'monitoring' | 'sentinel';
};

type EstateLike = {
  providers?: string[];
  freshness?: string;
  conflicts?: unknown[];
  providerFreshness?: Record<string, unknown>;
};

export type MobInteractionNodeLike = {
  key: string;
  type: string;
  properties?: Record<string, unknown>;
  estate?: EstateLike;
};

export type MobInteractionEdgeLike = {
  from: string;
  to: string;
};

function normalize(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function providers(node: MobInteractionNodeLike): Set<string> {
  return new Set((node.estate?.providers ?? []).map(normalize).filter(Boolean));
}

function hasProvider(node: MobInteractionNodeLike, provider: string): boolean {
  return providers(node).has(normalize(provider));
}

function providerFreshnessStatus(node: MobInteractionNodeLike, provider: string): string {
  const entry = node.estate?.providerFreshness?.[provider];
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return '';
  return normalize((entry as Record<string, unknown>)['status']);
}

function isDevice(node: MobInteractionNodeLike): boolean {
  return node.type === 'endpoint';
}

function isActiveIncident(node: MobInteractionNodeLike): boolean {
  if (node.type !== 'incident') return false;
  const status = normalize(node.properties?.['status']);
  return status !== 'closed' && status !== 'resolved';
}

function severityMatches(node: MobInteractionNodeLike, severity: MobSentinelSeverity): boolean {
  if (severity === 'all') return true;
  return normalize(node.properties?.['severity']) === severity;
}

export function countSentinelSeeds(
  nodes: MobInteractionNodeLike[],
  scope: MobSentinelScope,
  severity: MobSentinelSeverity,
): number {
  return nodes.filter((node) => {
    if (!severityMatches(node, severity)) return false;
    if (scope === 'alerts') return node.type === 'alert';
    if (scope === 'active_incidents') return isActiveIncident(node);
    return node.type === 'incident';
  }).length;
}

export function resolveSentinelLensKeys(
  nodes: MobInteractionNodeLike[],
  edges: MobInteractionEdgeLike[],
  scope: MobSentinelScope,
  severity: MobSentinelSeverity,
): { keys: Set<string>; seedKeys: Set<string>; seedCount: number } {
  const validKeys = new Set(nodes.map((node) => node.key));
  const seedKeys = new Set<string>();
  for (const node of nodes) {
    if (!severityMatches(node, severity)) continue;
    if (scope === 'alerts' ? node.type === 'alert' : scope === 'active_incidents' ? isActiveIncident(node) : node.type === 'incident') {
      seedKeys.add(node.key);
    }
  }

  const adjacency = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (!validKeys.has(edge.from) || !validKeys.has(edge.to)) continue;
    const from = adjacency.get(edge.from) ?? new Set<string>();
    const to = adjacency.get(edge.to) ?? new Set<string>();
    from.add(edge.to);
    to.add(edge.from);
    adjacency.set(edge.from, from);
    adjacency.set(edge.to, to);
  }

  const keys = new Set(seedKeys);
  let frontier = new Set(seedKeys);
  for (let depth = 0; depth < 2 && frontier.size; depth += 1) {
    const next = new Set<string>();
    for (const key of frontier) {
      for (const neighbor of adjacency.get(key) ?? []) {
        if (keys.has(neighbor)) continue;
        keys.add(neighbor);
        next.add(neighbor);
      }
    }
    frontier = next;
  }
  return { keys, seedKeys, seedCount: seedKeys.size };
}

export function resolveEstateDrilldownKeys(
  nodes: MobInteractionNodeLike[],
  request: Pick<MobEstateDrilldownRequest, 'kind' | 'value'>,
): { keys: Set<string> } {
  const value = normalize(request.value);
  const keys = new Set<string>();
  for (const node of nodes) {
    let match = false;
    if (request.kind === 'provider') {
      const provider = value === 'sentinel_telemetry' ? 'sentinel_logs' : value;
      match = isDevice(node) && hasProvider(node, provider);
    } else if (request.kind === 'freshness') {
      match = Boolean(node.estate) && normalize(node.estate?.freshness) === value;
    } else if (request.kind === 'gap') {
      if (value === 'azure_without_mde') match = isDevice(node) && hasProvider(node, 'azure') && !hasProvider(node, 'mde');
      else if (value === 'intune_without_mde') match = isDevice(node) && hasProvider(node, 'intune') && !hasProvider(node, 'mde');
      else if (value === 'mde_without_intune') match = isDevice(node) && hasProvider(node, 'mde') && !hasProvider(node, 'intune');
      else if (value === 'no_recent_sentinel_telemetry') {
        const status = providerFreshnessStatus(node, 'sentinel_logs');
        match = isDevice(node) && (!hasProvider(node, 'sentinel_logs') || (status !== 'fresh' && status !== 'recent'));
      } else if (value === 'stale_devices') match = isDevice(node) && normalize(node.estate?.freshness) === 'stale';
      else if (value === 'conflicted_canonical_entities') match = Boolean(node.estate && Array.isArray(node.estate.conflicts) && node.estate.conflicts.length > 0);
    } else if (request.kind === 'sentinel') {
      if (value === 'alerts') match = node.type === 'alert';
      else if (value === 'active_incidents') match = isActiveIncident(node);
      else match = node.type === 'incident';
    }
    if (match) keys.add(node.key);
  }
  return { keys };
}
